import { useState, useEffect } from "react";
import {
  useGetDesignOntologyGraphService,
  UserJourneyItem,
  FlowItem,
  PageItem,
  ComponentItem,
} from "@/service-hooks/use-get-design-ontology-graph-service";
import { useParams } from "react-router-dom";
import useDebounce from "./use-debounce";

export const useDesignOntologyGraphPagination = (scenarioId?: string) => {
  const { id = "" } = useParams();

  // Selected items
  const [selectedUserJourney, setSelectedUserJourney] =
    useState<UserJourneyItem | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<FlowItem | null>(null);
  const [selectedPage, setSelectedPage] = useState<PageItem | null>(null);

  // Search states
  const [userJourneySearch, setUserJourneySearch] = useState("");
  const [flowSearch, setFlowSearch] = useState("");
  const [pageSearch, setPageSearch] = useState("");
  const [componentSearch, setComponentSearch] = useState("");

  // Page states
  const [userJourneyPage, setUserJourneyPage] = useState(1);
  const [flowPage, setFlowPage] = useState(1);
  const [pagePage, setPagePage] = useState(1);
  const [componentPage, setComponentPage] = useState(1);

  const debouncedUserJourneySearch = useDebounce(userJourneySearch, 500);
  const debouncedFlowSearch = useDebounce(flowSearch, 500);
  const debouncedPageSearch = useDebounce(pageSearch, 500);
  const debouncedComponentSearch = useDebounce(componentSearch, 500);

  // UserJourney query
  const userJourneyQuery = useGetDesignOntologyGraphService({
    projectUuid: id,
    label: "UserJourney",
    limit: 10,
    page: userJourneyPage,
    filters: {
      ...(debouncedUserJourneySearch
        ? { name: { $containsi: debouncedUserJourneySearch } }
        : {}),
      ...(scenarioId ? { scenarioId: { $eq: scenarioId } } : {}),
    },
    enabled: !!id,
  });

  // Flow query — enabled when a UserJourney is selected
  const flowQuery = useGetDesignOntologyGraphService({
    projectUuid: id,
    label: "Flow",
    limit: 10,
    page: flowPage,
    filters: {
      ...(selectedUserJourney
        ? { userJourneyIds: selectedUserJourney.id }
        : {}),
      ...(debouncedFlowSearch
        ? { name: { $containsi: debouncedFlowSearch } }
        : {}),
    },
    enabled: !!selectedUserJourney && !!id,
  });

  // Page query — enabled when a Flow is selected
  const pageQuery = useGetDesignOntologyGraphService({
    projectUuid: id,
    label: "Page",
    limit: 10,
    page: pagePage,
    filters: {
      ...(selectedFlow ? { flowIds: selectedFlow.id } : {}),
      ...(debouncedPageSearch
        ? { name: { $containsi: debouncedPageSearch } }
        : {}),
    },
    enabled: !!selectedFlow && !!id,
  });

  // Component query — enabled when a Page is selected
  const componentQuery = useGetDesignOntologyGraphService({
    projectUuid: id,
    label: "Component",
    limit: 10,
    page: componentPage,
    filters: {
      ...(selectedPage ? { pageIds: selectedPage.id } : {}),
      ...(debouncedComponentSearch
        ? { name: { $containsi: debouncedComponentSearch } }
        : {}),
    },
    enabled: !!selectedPage && !!id,
  });

  // Click handlers — reset child selections
  const handleUserJourneyClick = (uj: UserJourneyItem) => {
    setSelectedUserJourney(uj);
    setSelectedFlow(null);
    setSelectedPage(null);
    setFlowPage(1);
  };

  const handleFlowClick = (flow: FlowItem) => {
    setSelectedFlow(flow);
    setSelectedPage(null);
    setPagePage(1);
  };

  const handlePageClick = (page: PageItem) => {
    setSelectedPage(page);
    setComponentPage(1);
  };

  // Page navigation
  const handleUserJourneyNextPage = () =>
    setUserJourneyPage((prev) => prev + 1);
  const handleUserJourneyPrevPage = () =>
    setUserJourneyPage((prev) => Math.max(1, prev - 1));

  const handleFlowNextPage = () => setFlowPage((prev) => prev + 1);
  const handleFlowPrevPage = () =>
    setFlowPage((prev) => Math.max(1, prev - 1));

  const handlePageNextPage = () => setPagePage((prev) => prev + 1);
  const handlePagePrevPage = () =>
    setPagePage((prev) => Math.max(1, prev - 1));

  const handleComponentNextPage = () =>
    setComponentPage((prev) => prev + 1);
  const handleComponentPrevPage = () =>
    setComponentPage((prev) => Math.max(1, prev - 1));

  // Reset page + selections when scenarioId filter changes
  useEffect(() => {
    setUserJourneyPage(1);
    setSelectedUserJourney(null);
    setSelectedFlow(null);
    setSelectedPage(null);
  }, [scenarioId]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setUserJourneyPage(1);
  }, [debouncedUserJourneySearch]);

  useEffect(() => {
    setFlowPage(1);
  }, [debouncedFlowSearch]);

  useEffect(() => {
    setPagePage(1);
  }, [debouncedPageSearch]);

  useEffect(() => {
    setComponentPage(1);
  }, [debouncedComponentSearch]);

  // Data
  const userJourneys =
    (userJourneyQuery.data?.data as UserJourneyItem[]) || [];
  const flows = (flowQuery.data?.data as FlowItem[]) || [];
  const pages = (pageQuery.data?.data as PageItem[]) || [];
  const components = (componentQuery.data?.data as ComponentItem[]) || [];

  // Totals
  const userJourneysTotal = userJourneyQuery.data?.total || 0;
  const flowsTotal = flowQuery.data?.total || 0;
  const pagesTotal = pageQuery.data?.total || 0;
  const componentsTotal = componentQuery.data?.total || 0;

  // Total pages
  const userJourneysTotalPages = Math.ceil(userJourneysTotal / 10);
  const flowsTotalPages = Math.ceil(flowsTotal / 10);
  const pagesTotalPages = Math.ceil(pagesTotal / 10);
  const componentsTotalPages = Math.ceil(componentsTotal / 10);

  return {
    // Data
    userJourneys,
    flows,
    pages,
    components,

    // Totals
    userJourneysTotal,
    flowsTotal,
    pagesTotal,
    componentsTotal,

    // Page info
    userJourneyPage,
    flowPage,
    pagePage,
    componentPage,
    userJourneysTotalPages,
    flowsTotalPages,
    pagesTotalPages,
    componentsTotalPages,

    // Selected items
    selectedUserJourney,
    selectedFlow,
    selectedPage,

    // Click handlers
    handleUserJourneyClick,
    handleFlowClick,
    handlePageClick,

    // Page navigation
    handleUserJourneyNextPage,
    handleUserJourneyPrevPage,
    handleFlowNextPage,
    handleFlowPrevPage,
    handlePageNextPage,
    handlePagePrevPage,
    handleComponentNextPage,
    handleComponentPrevPage,

    // Query objects
    userJourneyQuery,
    flowQuery,
    pageQuery,
    componentQuery,

    // Loading states
    isLoadingUserJourneys: userJourneyQuery.isLoading,
    isLoadingFlows: flowQuery.isLoading,
    isLoadingPages: pageQuery.isLoading,
    isLoadingComponents: componentQuery.isLoading,

    // Search states
    userJourneySearch,
    setUserJourneySearch,
    flowSearch,
    setFlowSearch,
    pageSearch,
    setPageSearch,
    componentSearch,
    setComponentSearch,

    projectUuid: id,
    setSelectedUserJourney,
    setSelectedFlow,
    setSelectedPage,
  };
};
